import { ChangeEvent, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';

import { MyButton } from '../../uiComponents/MyButton';
import { FormInput } from '../../uiComponents/FormInput';
import { validateClientRequestTitle } from '../../../schemas/clientRequestTitleSchema';

import styles from './SaveFilterDialog.module.scss';
import type { SaveFilterDialogState, SaveFilterResult, SaveFilterSelection } from './types';

interface SaveFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: (result: SaveFilterResult) => void;
  onExited: () => void;
  onSuccessAction: () => void;
  state: SaveFilterDialogState;
}

interface SaveFilterFormValues {
  title: string;
}

const options: Array<{
  description: string;
  label: string;
  selectionKey: keyof SaveFilterSelection;
}> = [
  {
    description: 'Keep the current search criteria so you can use them again.',
    label: 'Save as filter',
    selectionKey: 'saveAsFilter',
  },
  {
    description:
      'Transfer compatible criteria to a Client Request, then complete the required details there.',
    label: 'Save as client request',
    selectionKey: 'saveAsClientRequest',
  },
];

const emptySelection: SaveFilterSelection = {
  saveAsClientRequest: false,
  saveAsFilter: false,
};

export const SaveFilterDialog = ({
  open,
  onClose,
  onContinue,
  onExited,
  onSuccessAction,
  state,
}: SaveFilterDialogProps) => {
  const [selection, setSelection] = useState<SaveFilterSelection>(emptySelection);
  const isSubmitting = state.status === 'saving';
  const isSuccess = state.status === 'success-save-only' || state.status === 'success-combined';
  const successDescription =
    state.status === 'success-combined'
      ? 'Your filter has been saved. Continue to create a client request.'
      : 'Your filter is now available in Saved filters.';
  const hasSelection = selection.saveAsFilter || selection.saveAsClientRequest;
  const formMethods = useForm<SaveFilterFormValues>({
    defaultValues: { title: '' },
  });
  const { clearErrors, reset, setError, watch } = formMethods;
  const titleValidation = validateClientRequestTitle(watch('title'));
  const isTitleValid = !titleValidation.error;

  useEffect(() => {
    if (!open) {
      setSelection(emptySelection);
      reset({ title: '' });
    }
  }, [open, reset]);

  const handleClose = () => {
    if (isSubmitting) return;

    setSelection(emptySelection);
    reset({ title: '' });
    onClose();
  };

  const handleContinue = () => {
    if (hasSelection && isTitleValid && !isSubmitting) {
      onContinue({
        ...selection,
        title: titleValidation.title,
      });
    }
  };

  const handleTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const { error } = validateClientRequestTitle(value);

    if (error) {
      setError('title', { message: error, type: 'validation' });
    } else {
      clearErrors('title');
    }
  };

  const toggleSelection = (selectionKey: keyof SaveFilterSelection) => {
    setSelection((currentSelection) => ({
      ...currentSelection,
      [selectionKey]: !currentSelection[selectionKey],
    }));
  };

  return (
    <FormProvider {...formMethods}>
      <Dialog
        aria-busy={isSubmitting}
        aria-describedby='save-filter-dialog-description'
        aria-labelledby='save-filter-dialog-title'
        className={styles.dialog}
        disableEscapeKeyDown={isSubmitting}
        fullWidth
        maxWidth='sm'
        onClose={handleClose}
        open={open}
        PaperProps={{
          className: classNames(styles.paper, {
            [styles.successPaper]: isSuccess,
          }),
        }}
        TransitionProps={{ onExited }}
      >
        <DialogTitle
          className={classNames(styles.title, {
            [styles.successTitle]: isSuccess,
          })}
          id='save-filter-dialog-title'
        >
          {isSuccess ? 'FILTER SAVED' : 'Save current search'}
          <IconButton
            aria-label='Close'
            className={classNames(styles.closeButton, {
              [styles.successCloseButton]: isSuccess,
            })}
            disabled={isSubmitting}
            onClick={handleClose}
            size='small'
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className={classNames(styles.content, {
            [styles.successContent]: isSuccess,
          })}
        >
          {isSuccess ? (
            <Stack className={styles.successCopy} spacing={1}>
              <Typography className={styles.savedTitle} component='p'>
                {state.title}
              </Typography>
              <Typography
                className={styles.successMessage}
                component='p'
                id='save-filter-dialog-description'
              >
                {successDescription}
              </Typography>
            </Stack>
          ) : (
            <>
              <p className={styles.description} id='save-filter-dialog-description'>
                Choose how to use the filters currently applied to this search.
              </p>
              <div className={styles.titleField}>
                <FormInput
                  fullWidth
                  inputProps={{ 'aria-label': 'Title' }}
                  label='Title'
                  name='title'
                  onChange={handleTitleChange}
                  placeholder='Enter title'
                  required
                  uiVariant='blank'
                />
              </div>
              <div aria-label='Save current search as' className={styles.options} role='group'>
                {options.map(({ description, label, selectionKey }) => (
                  <label
                    className={classNames(styles.option, {
                      [styles.optionSelected]: selection[selectionKey],
                    })}
                    key={selectionKey}
                  >
                    <input
                      checked={selection[selectionKey]}
                      className={styles.optionInput}
                      name={selectionKey}
                      onChange={() => toggleSelection(selectionKey)}
                      type='checkbox'
                    />
                    <span aria-hidden='true' className={styles.optionIndicator} />
                    <span className={styles.optionCopy}>
                      <span className={styles.optionTitle}>{label}</span>
                      <span className={styles.optionDescription}>{description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions
          className={classNames(styles.actions, {
            [styles.successActions]: isSuccess,
          })}
        >
          {isSuccess ? (
            <MyButton
              data={{
                buttonName:
                  state.status === 'success-combined' ? 'Continue to client request' : 'Done',
                buttonType: 'button',
                customWidth: state.status === 'success-combined' ? '300px' : '180px',
                styleType: 'dialog',
                variant: 'contained',
              }}
              onClick={state.status === 'success-combined' ? onSuccessAction : handleClose}
            />
          ) : (
            <MyButton
              data={{
                buttonName: 'Continue',
                buttonType: 'button',
                customWidth: '180px',
                styleType: 'dialog',
                variant: 'contained',
              }}
              disabled={!hasSelection || !isTitleValid || isSubmitting}
              onClick={handleContinue}
            />
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
